import React from 'react';
import Navigation from './Navigation';

/**
 * InfoPage - Komponente für Projektinformationen
 * @returns {JSX.Element} Info-Seite
 */
function InfoPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-pink-50 rounded-xl shadow-sm">
          {/* Header-Bereich */}
          <div className="py-6">
            <div className="container mx-auto px-4">
              <h1 className="text-3xl font-bold text-center">Projektinformationen</h1>
              <p className="mt-2 text-center text-gray-700">
                Hintergrundinformationen und technische Details zum Sunnyride-Projekt
              </p>
            </div>
          </div>
          
          {/* Inhaltsbereich */}
          <div className="px-4 py-6">
            <div className="max-w-4xl mx-auto bg-white rounded-xl p-6 shadow-sm">
              <div className="prose max-w-none">
                <h2 className="text-xl font-semibold mb-4">Über das Projekt</h2>
                
                <p>
                  Das Sunnyride-Projekt ist eine innovative Datenanalyse-Initiative, die auf der Grundlage von öffentlich zugänglichen Datensätzen der <a href="https://opendata.braunschweig.de/search/type/dataset" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Open Data Braunschweig</a> entstanden ist. Im Fokus meiner Arbeit stehen zwei zentrale Datensätze: die <a href="https://opendata.braunschweig.de/dataset/automatische-radverkehrsz%C3%A4hlung" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Automatische Radverkehrszählung</a> und die Wetterdaten der <a href="https://opendata.braunschweig.de/dataset/wetterstation-igs-heidberg" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Wetterstation IGS Heidberg</a>. Durch die Kombination und Analyse dieser Daten kann ich faszinierende Zusammenhänge zwischen Wetterbedingungen und Fahrradnutzung in Braunschweig aufzeigen.
                </p>
                
                <h3 className="text-lg font-semibold mt-6 mb-3">Datenverarbeitung</h3>
                <p>
                  Mein technischer Workflow zur Datenverarbeitung basiert auf einer modernen Cloud-Architektur. Die Open Data Datensätze werden zunächst über eine AWS Lambda-Funktion automatisiert abgerufen und in AWS S3 Buckets als zentraler Speicherort abgelegt. Für die anschließende Datenaufbereitung und -transformation nutze ich AWS Glue, einen serverlosen ETL-Dienst (Extract, Transform, Load), der die Rohdaten in strukturierte, analysefreundliche Formate umwandelt.
                </p>
                <p>
                  Mit AWS Athena führe ich dann komplexe SQL-Abfragen direkt auf den S3-gespeicherten Daten durch, um tiefgreifende statistische Analysen und Trendermittlungen vorzunehmen. Diese Architektur ermöglicht es mir, große Datenmengen effizient zu verarbeiten und wertvolle Erkenntnisse zu gewinnen, ohne eine aufwendige lokale Infrastruktur betreiben zu müssen.
                </p>
                
                <h3 className="text-lg font-semibold mt-6 mb-3">SunnyScore Algorithmus</h3>
                <p>
                  Der SunnyScore ist das Herzstück meiner Anwendung – ein eigens entwickelter Algorithmus, der auf Basis historischer Muster in den Radverkehrs- und Wetterdaten präzise Vorhersagen trifft. Zur Anreicherung und Optimierung dieser Prognosen integriere ich aktuelle und zukünftige Wetterdaten der API von <a href="https://www.visualcrossing.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">visualcrossing.com</a>.
                </p>
                <p>
                  Der Algorithmus identifiziert Korrelationen zwischen spezifischen Wetterparametern (wie Temperatur, Niederschlag, Windgeschwindigkeit) und dem Radverkehrsaufkommen. Durch maschinelles Lernen verbessert sich die Vorhersagegenauigkeit kontinuierlich, sodass du stets die bestmögliche Entscheidungsgrundlage für deine Fahrradfahrten erhältst.
                </p>
                
                <h3 className="text-lg font-semibold mt-6 mb-3">Technische Umsetzung</h3>
                <p>
                  Die Webapplikation wurde als moderne Single-Page-Application mit React entwickelt und wird über die leistungsstarke Cloud-Infrastruktur von AWS bereitgestellt. Die statischen Inhalte werden in einem AWS S3 Bucket gehostet und durch AWS CloudFront als Content Delivery Network global mit minimaler Latenz ausgeliefert.
                </p>
                <p>
                  Dieses Projekt entstand aus einer Kombination von fachlicher Neugier und dem Wunsch, neue Technologien praktisch einzusetzen. Ich habe bewusst einige Tools gewählt, die ich erkunden wollte, auch wenn sie möglicherweise nicht in jedem Fall die effizienteste Lösung darstellen. Dieser experimentelle Ansatz hat mir wertvolle Erkenntnisse für zukünftige Projekte geliefert und zeigt die vielfältigen Möglichkeiten bei der Arbeit mit offenen Daten.
                </p>
                
                <h3 className="text-lg font-semibold mt-6 mb-3">Unterstützung</h3>
                <p>
                  Ein besonderer Dank gilt Claude 3.7 Sonnet, dem KI-Assistenten von Anthropic, der mich bei verschiedenen Aspekten der Entwicklung – von der Datenanalyse bis zur Codeoptimierung – maßgeblich unterstützt hat. Diese Zusammenarbeit zwischen menschlicher Kreativität und künstlicher Intelligenz ermöglichte es mir, komplexe Herausforderungen effizient zu bewältigen und innovative Lösungsansätze zu finden.
                </p>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold mb-3">Kontakt & Feedback</h3>
                  <p>
                    Deine Meinung ist mir wichtig! Ich freue mich über Feedback, Anregungen oder Fragen zu diesem Projekt. Schreib mir einfach eine E-Mail an{' '}
                    <a 
                      href="mailto:sunnyride@mailbox.org" 
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      sunnyride@mailbox.org
                    </a>
                    .
                  </p>
                  <p>
                    Entdecke mit mir die spannenden Möglichkeiten, die offene Daten für Braunschweig und darüber hinaus bieten können. Gemeinsam können wir datenbasierte Lösungen entwickeln, die zu einer nachhaltigeren und intelligenteren Mobilität beitragen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InfoPage;